<template>
	<div>
		<form action="#" method="post" @submit.prevent="submit">

			<!-- Form Fields -->
			<h2>Add Bank Account</h2>

			<h4 class="mt-md-5 pt-4 mb-3">How would you like to add a bank account?</h4>
			<div class="row">
				<div class="col-lg-6">
					<div class="form-check radio-checkbox mt-3">
						<input class="form-check-input" type="radio" name="type" value="manual" id="connectionTypeManual" v-model="type">
						<label class="form-check-label" for="connectionTypeManual">
							<strong>Enter a bank account manually</strong>
							<span>Manually enter your bank's routing and account number.</span>
						</label>
					</div>
				</div>
				<div class="col-lg-6">
					<div class="form-check radio-checkbox mt-3">
						<input class="form-check-input" type="radio" name="type" value="link" id="connectionTypeLink" v-model="type">
						<label class="form-check-label" for="connectionTypeLink">
							<strong>Link a bank account</strong>
							<span>Use your online banking credentials to connect securely a bank account.</span>
						</label>
					</div>
				</div>
			</div>

			<hr class="my-5">

			<div class="link-bank" v-if="type === 'link'">
				<div class="logos">
					<img src="../assets/logo.svg" alt="Reiturn by Birgo" class="logo">
					<img src="../assets/plaid.png" alt="Plaid" class="plaid-logo">
				</div>
				<h3>Reiturn uses <strong>Plaid</strong> to connect your account</h3>
				<div class="link-box-feature">
					<strong>Connect effortlessly</strong>
					<p>Plaid lets you securely connect your financial accounts in seconds.</p>
				</div>
				<div class="link-box-feature">
					<strong>Your data belongs to you</strong>
					<p>Plaid doesn't sell your personal information, and will only use it with your permission.</p>
				</div>
				<p class="disclaimer">By moving forward, you agree to the <a href="#" target="_blank">Plaid End User Privacy Policy</a>.</p>
			</div>

			<div class="manual-bank" v-if="type === 'manual'">
				<h4>Bank &amp; Account type</h4>
				<div class="form-field mt-3 pt-3">
					<label for="accountName" class="form-label">Account Name</label>
					<input type="text" class="form-control" name="accountName" id="accountName" placeholder="Account Name as it appears on your paper checks" required v-model="manual.accountName">
				</div>
				<div class="form-field mt-3 pt-3">
					<label for="bankName" class="form-label">Bank</label>
					<input type="text" class="form-control" name="bankName" id="bankName" placeholder="Name of bank as it appears on your paper checks" required v-model="manual.name">
				</div>
				<div class="form-field mt-3 pt-3">
					<label for="accountType" class="form-label">Account Type</label>
					<select class="form-control" name="accountType" id="accountType" required v-model="manual.type">
						<option>Checking</option>
						<option>Savings</option>
					</select>
				</div>
				<div class="form-field mt-3 pt-3">
					<label for="routingNumber" class="form-label">Routing Number</label>
					<input type="text" class="form-control" name="routingNumber" id="routingNumber" placeholder="Enter 9 digit routing number" required v-model="manual.routingNumber">
				</div>
				<img src="../assets/bank-account-explanation.jpg" alt="Bank Account Explanation" class="img-fluid d-block mx-auto w-75 mt-5">
				<div class="form-field mt-3 pt-3">
					<label for="accountNumber" class="form-label">Account Number</label>
					<input type="text" class="form-control" name="accountNumber" id="accountNumber" placeholder="Enter account number" required v-model="manual.accountNumber">
				</div>
				<div class="form-field mt-3 pt-3">
					<label for="confirmAccountNumber" class="form-label">Confirm Account Number</label>
					<input type="text" class="form-control" name="confirmAccountNumber" id="confirmAccountNumber" placeholder="Confirm by typing again" required v-model="manual.confirmAccountNumber">
					<p class="text-danger fw-bolder small mt-2" v-if="showErrors && manual.accountNumber !== manual.confirmAccountNumber">The account numbers do not match.</p>
				</div>
			</div>

			<p class="text-danger fw-bolder small mt-4" v-if="!loading && errors">{{ errors }}</p>

			<!-- Form Navigation -->
			<div class="d-flex flex-column-reverse flex-md-row justify-content-md-between pt-5">
				<button class="btn btn-outline-dark me-md-2 mt-2 mt-md-0" type="button" @click.prevent="back">Back</button>
				<button class="btn btn-primary btn-wide" type="submit" :disabled="loading">
					<span class="spinner-border" v-if="loading"></span>
					<span :class="{'opacity-0': loading}">Continue</span>
				</button>
			</div>
		</form>

		<form :action="plaidIntegrationEndpoint" method="post" ref="plaidForm">
			<input type="hidden" name="token" v-model="token">
			<input type="hidden" name="currentStepRoute" value="/details/bank-account">
			<input type="hidden" name="nextStepRoute" value="/details/agreements">
		</form>
	</div>
</template>

<script>

import repository, {API_URL} from "@/repository/repository";
import Logs from "@/repository/logs";

export default {
    name: "BankForm",
    beforeMount() {
        // Verify the previous step is completed
        if(!this.$store.getters.investment.completed) {
            this.$router.replace("/details/investment-plan");
        }
    },
    data() {
        return {
            type: this.$store.getters.bankAccount.type,
            manual: {
                accountName: this.$store.getters.bankAccount.manual.accountName,
                name: this.$store.getters.bankAccount.manual.name,
                type: this.$store.getters.bankAccount.manual.type,
                routingNumber: this.$store.getters.bankAccount.manual.routingNumber,
                accountNumber: this.$store.getters.bankAccount.manual.accountNumber,
                confirmAccountNumber: this.$store.getters.bankAccount.manual.accountNumber,
			},
            showErrors: false,
			loading: false,
            errors: false,
        }
    },
    methods: {
        back() {
            this.$store.commit("updateBankAccount", {
                type: this.type,
                manual: {
                    accountName: this.manual.accountName,
                    name: this.manual.name,
                    type: this.manual.type,
                    routingNumber: this.manual.routingNumber,
                    accountNumber: this.manual.accountNumber,
				},
            });
            this.$router.replace("/details/investment-plan");
        },
        submit() {
            if(!this.checkAccountNumber()) {
                this.showErrors = true;
                return false;
			}

            this.loading = true;
            this.errors = false;

            this.$store.commit("updateBankAccount", {
                type: this.type,
                manual: {
                    accountName: this.manual.accountName,
                    name: this.manual.name,
                    type: this.manual.type,
                    routingNumber: this.manual.routingNumber,
                    accountNumber: this.manual.accountNumber,
                },
            });

            if(this.type === 'link') {
                // Try to link the bank account via Plaid
				this.$refs.plaidForm.submit();
			} else {
                // Save the bank account manually
				let payload = {
                    emailAddress: this.$store.getters.auth.email,
                    accountId: this.$store.getters.northCapital.accountId,
                    accountName: this.$store.getters.bankAccount.manual.accountName,
                    name: this.$store.getters.bankAccount.manual.name,
                    type: this.$store.getters.bankAccount.manual.type,
                    routingNumber: this.$store.getters.bankAccount.manual.routingNumber,
                    accountNumber: this.$store.getters.bankAccount.manual.accountNumber,
                };
                repository.post("/save-bank", payload)
                    .then(response => {
                        console.log(response);
                        this.loading = false;
                        this.$store.commit("finishBank");
                        this.$router.push("/details/agreements");
                    })
                    .catch(error => {
                        console.log(error);
                        this.loading = false;
                        this.errors = error.response.data.issue;
                        Logs.save('onboarding.bankFormManual', error.response, payload);
                    })
			}
        },
		checkAccountNumber() {
            if(this.type === 'link') return true;
            if(!this.manual.accountNumber) return false;
            return (this.manual.accountNumber === this.manual.confirmAccountNumber);
		},
    },
	computed: {
		accountId() {
            return this.$store.getters.northCapital.accountId;
		},
		emailAddress() {
            return this.$store.getters.auth.email;
		},
        plaidIntegrationEndpoint() {
            return API_URL + "/process-bank";
		},
		token() {
            return this.$store.getters.token;
		},
	},
}
</script>

<style lang="scss">
.form-check.radio-checkbox {
	cursor: pointer;
	position: relative;
	padding-left: 2.5rem;

	.form-check-input {
		border-width: 4px;
		width: 1.25rem;
		height: 1.25rem;
		box-shadow: #dcddde 0 0 0 0.15rem;
		border-color: #fff;
		transition: 0.15s ease all;
		float: none;
		position: absolute;
		top: 0.5rem;
		left: 0.15rem;
		margin: 0;

		&:checked {
			box-shadow: #F37021 0 0 0 0.15rem;
			background: #F37021;
			border-color: #fff;
		}
	}

	.form-check-label {
		padding: 0;
		margin: 0;
		display: block;
		cursor: pointer;

		&.label-lg {
			color: #16193A;
			font-size: 1.15rem;

			@media (max-width: 767px) {
				font-size: 1rem;
			}
		}

		strong {
			display: block;
			font-size: 1.15rem;
			font-weight: bold;
			color: #16193A;

			@media (max-width: 767px) {
				font-size: 1rem;
			}
		}

		a {
			color: #F37021;
			font-weight: 600;
		}
	}
}

.link-bank {
	background: #16193A;
	padding: 2.5rem;
	border-radius: 0.5rem;

	.logos {
		display: flex;
		align-items: center;

		img {
			height: 2rem;
			margin-right: 1.5rem;
		}
	}

	h3 {
		margin: 2rem 0 1.5rem 0;
		color: #fff;
		font-size: 1.5rem;
		font-weight: 400;

		strong {
			font-weight: bold;
		}
	}

	.link-box-feature {
		position: relative;
		padding-left: 2.25rem;
		margin-bottom: 1.5rem;

		&:before {
			width: 1.25rem;
			height: 1.25rem;
			position: absolute;
			top: 0.45rem;
			left: 0.15rem;
			content: ' ';
			border-radius: 50%;
			border: 4px solid #16193A;
			box-shadow: #F37021 0 0 0 0.15rem;
			background: #F37021;
		}

		strong {
			display: block;
			color: #fff;
			font-weight: bold;
			font-size: 1rem;
		}

		p {
			margin: 0;
			font-size: 0.9rem;
			color: rgba(#fff, 0.5);
		}
	}

	p.disclaimer {
		font-size: 0.9rem;
		color: rgba(#fff, 0.5);

		a {
			color: inherit;
			text-decoration: underline;
		}
	}
}
</style>